import React, { useRef } from 'react';

const HANDLES = ['nw', 'ne', 'sw', 'se'];
const MIN = 0.06;
const clamp01 = (v) => Math.max(0, Math.min(1, v));

const handleStyle = (h) => {
  const off = -8;
  const s = { width: 16, height: 16 };
  if (h.includes('n')) s.top = off; else s.bottom = off;
  if (h.includes('w')) s.left = off; else s.right = off;
  s.cursor = h === 'nw' || h === 'se' ? 'nwse-resize' : 'nesw-resize';
  return s;
};

/**
 * Draggable crop box over a card "context" image.
 * rect + onChange use fractions of the image: { x, y, w, h } in [0,1].
 */
export const CardCropAdjuster = ({ imageUrl, rect, onChange, testId }) => {
  const ref = useRef(null);
  const r = rect && typeof rect.x === 'number' ? rect : { x: 0.1, y: 0.1, w: 0.8, h: 0.8 };

  const beginDrag = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const box = ref.current.getBoundingClientRect();
    const startRect = { ...r };
    const sx = clamp01((e.clientX - box.left) / box.width);
    const sy = clamp01((e.clientY - box.top) / box.height);

    const move = (ev) => {
      const fx = clamp01((ev.clientX - box.left) / box.width);
      const fy = clamp01((ev.clientY - box.top) / box.height);
      let left = startRect.x, top = startRect.y;
      let right = startRect.x + startRect.w, bottom = startRect.y + startRect.h;

      if (mode === 'move') {
        let nl = clamp01(startRect.x + (fx - sx));
        let nt = clamp01(startRect.y + (fy - sy));
        nl = Math.max(0, Math.min(nl, 1 - startRect.w));
        nt = Math.max(0, Math.min(nt, 1 - startRect.h));
        left = nl; top = nt; right = nl + startRect.w; bottom = nt + startRect.h;
      } else {
        if (mode.includes('w')) left = fx;
        if (mode.includes('e')) right = fx;
        if (mode.includes('n')) top = fy;
        if (mode.includes('s')) bottom = fy;
        if (right - left < MIN) { if (mode.includes('w')) left = right - MIN; else right = left + MIN; }
        if (bottom - top < MIN) { if (mode.includes('n')) top = bottom - MIN; else bottom = top + MIN; }
      }
      left = clamp01(left); top = clamp01(top); right = clamp01(right); bottom = clamp01(bottom);
      onChange({ x: left, y: top, w: Math.max(MIN, right - left), h: Math.max(MIN, bottom - top) });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const pct = (v) => `${v * 100}%`;

  return (
    <div
      ref={ref}
      data-testid={testId}
      className="relative w-full select-none bg-white rounded overflow-hidden"
      style={{ touchAction: 'none' }}
    >
      <img src={imageUrl} alt="card" className="block w-full pointer-events-none" draggable={false} />
      <div
        className="absolute border-2 border-emerald-400"
        style={{
          left: pct(r.x), top: pct(r.y), width: pct(r.w), height: pct(r.h),
          cursor: 'move', boxShadow: '0 0 0 9999px rgba(15,23,42,0.45)',
        }}
        onPointerDown={beginDrag('move')}
      >
        {HANDLES.map((h) => (
          <div
            key={h}
            data-testid={testId ? `${testId}-handle-${h}` : undefined}
            onPointerDown={beginDrag(h)}
            className="absolute bg-emerald-400 border-2 border-white rounded-sm shadow"
            style={handleStyle(h)}
          />
        ))}
      </div>
    </div>
  );
};

export default CardCropAdjuster;
